import DOMPurify from 'dompurify';

const HTML_TAG_PATTERN = /<\/?[a-z][a-z0-9]*[\s/>]/i;

/**
 * Returns true when the string appears to contain HTML markup.
 * Used when API fields may be either plain text or HTML.
 */
export function containsHtml(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

/**
 * Sanitizes an HTML string before it is rendered via dangerouslySetInnerHTML.
 *
 * Allows the subset of tags and attributes that are needed for the PAD-terms /
 * legal-document content returned by the API (headings, paragraphs, lists,
 * tables, emphasis, links). Everything else—including any script/event-handler
 * content—is stripped by DOMPurify.
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'strong', 'em', 'u', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'a', 'span', 'div', 'hr',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    // Force all links to open safely (prevents tab-nabbing via window.opener)
    FORCE_BODY: true,
    ADD_ATTR: ['target'],
  });
}

/**
 * Strips all HTML tags and collapses whitespace for plain-text contexts
 * such as native browser `title` tooltips.
 */
export function stripHtml(value: string): string {
  if (!value) return '';
  return DOMPurify.sanitize(value, { ALLOWED_TAGS: [] })
    .replace(/\s+/g, ' ')
    .trim();
}
